import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * One suggestion row: the entity we *might* have meant. `id` and a display
 * `name` are required; `aliases` is rendered as a muted secondary line so the
 * operator can confirm a match like "Сбер" ≈ ["Сбербанк ПАО", "SberBank"].
 */
export interface DuplicateCandidate {
  id: string;
  name: string;
  aliases?: string[];
}

export interface DuplicateSuggestionsDialogProps {
  /** Controls visibility. */
  open: boolean;
  /** Operator-typed query that triggered the lookup (used in the
   * "create anyway" button label so it reads: «Всё равно создать "<query>"»). */
  query: string;
  /** Top-N matches. Caller is responsible for ranking and slicing to ≤3. */
  candidates: DuplicateCandidate[];
  /** Operator picked an existing entity from the list. */
  onPickExisting: (id: string) => void;
  /** Operator confirmed this is genuinely new — proceed with create. */
  onCreateAnyway: () => void;
  /** Cancel the whole submit — return to the form unchanged. */
  onCancel: () => void;
}

/**
 * Explicit duplicate-confirmation modal (Dmitri feedback `6054fe05`):
 * the implicit Combobox-fuzzy was unclear, so on submit of a Create-form we
 * surface the top candidates and force a deliberate "use existing" /
 * "create anyway" choice.
 */
export function DuplicateSuggestionsDialog({
  open,
  query,
  candidates,
  onPickExisting,
  onCreateAnyway,
  onCancel,
}: DuplicateSuggestionsDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={(v) => (v ? undefined : onCancel())}>
      <DialogContent data-testid="duplicate-suggestions-dialog">
        <DialogHeader>
          <DialogTitle>{t("duplicateSuggest.title")}</DialogTitle>
        </DialogHeader>
        <ul className="space-y-2" data-testid="duplicate-candidates">
          {candidates.map((c) => (
            <li
              key={c.id}
              className="flex items-start justify-between gap-3 rounded-md border p-2"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{c.name}</div>
                {c.aliases !== undefined && c.aliases.length > 0 ? (
                  <div className="truncate text-xs text-muted-foreground">
                    {c.aliases.join(", ")}
                  </div>
                ) : null}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onPickExisting(c.id)}
                data-testid={`duplicate-pick-${c.id}`}
              >
                {t("duplicateSuggest.pickExisting")}
              </Button>
            </li>
          ))}
        </ul>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t("duplicateSuggest.cancel")}
          </Button>
          <Button
            type="button"
            onClick={onCreateAnyway}
            data-testid="duplicate-create-anyway"
          >
            {t("duplicateSuggest.createAnyway", { query })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Normalize a Russian-corporate-style name for naive fuzzy match: lowercase,
 * strip whitespace, drop common org-form prefixes (`ооо`, `ао`, `зао`, `пао`,
 * `ип`). Used FE-side to decide whether the backend's ILIKE `?search=` hits
 * actually *resemble* the operator's query — the API returns substring
 * matches over name+aliases, but we want to suppress trivial overlaps
 * (e.g. typing "А" should not prompt 100 false-positive duplicates).
 *
 * Deliberately NOT Levenshtein — v1 scope says simple normalize+ILIKE is
 * enough; richer matching is a v2 backend concern.
 */
export function normalizeForFuzzy(raw: string): string {
  // JS `\b` only handles ASCII word boundaries, which fails for Cyrillic
  // input ("ООО Ромашка" → boundaries between Cyrillic letters never
  // trigger). We work around this with explicit prefix anchors: a known
  // org-form token followed by whitespace OR end-of-string, repeated until
  // nothing strips. This is naive but predictable and matches the v1 scope
  // (simple normalize + ILIKE; no full Levenshtein).
  const ORG_FORMS = /^(?:ооо|оао|зао|пао|ао|ип)\.?\s+/;
  let result = raw
    .toLowerCase()
    .trim()
    .replace(/["'«»().]/g, "");
  while (ORG_FORMS.test(result)) {
    result = result.replace(ORG_FORMS, "");
  }
  return result.replace(/\s+/g, "");
}

/**
 * Heuristic: a candidate is "similar enough" if the normalized query is a
 * substring of any of its normalized name/aliases (or vice versa, for short
 * queries). Pure function so it's trivially unit-testable.
 */
export function isSimilarCandidate(
  query: string,
  candidate: { name: string; aliases?: string[] },
): boolean {
  const nq = normalizeForFuzzy(query);
  if (nq.length === 0) return false;
  const haystacks = [candidate.name, ...(candidate.aliases ?? [])].map(
    normalizeForFuzzy,
  );
  return haystacks.some(
    (h) => h.length > 0 && (h.includes(nq) || nq.includes(h)),
  );
}
